import { partition } from 'lodash';
import type { ActionModifiers, CommandKeys } from '../types/CommandKeys';
import { isActionModifier, isModifierKey } from './isModifier';

const DISPLAYED_SHORTCUTS_MAP: Partial<Record<CommandKeys, string>> = {
  ' ': '␣',
  '=': '+',
  Alt: 'Alt',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  ArrowUp: '↑',
  Control: 'Ctrl',
  Delete: 'Delete',
  Enter: '⏎',
  Escape: 'Esc',
  Meta: 'Ctrl',
  Shift: '⇧',
};

const DISPLAYED_SHORTCUTS_MACOS: Partial<Record<CommandKeys, string>> = {
  ...DISPLAYED_SHORTCUTS_MAP,
  Alt: '⌥',
  Backspace: '⌫',
  Control: '⌃',
  Delete: 'Del',
  // One character, because the Mac string has no separator between keys and
  // would otherwise be split into a keycap per letter.
  Escape: '⎋',
  Meta: '⌘',
};

/**
 * Formats a given key name according to design guidelines. Modifier keys are
 * formatted according to OS conventions. Other characters are made uppercase.
 */
function getPrettyKeyName(keyName: string, isApple: boolean): string {
  const shortcutKey = keyName as CommandKeys;

  const preferredDisplayString =
    // use MacOS-specific display strings
    (isApple && DISPLAYED_SHORTCUTS_MACOS[shortcutKey]) ||
    // otherwise, use the default display strings
    DISPLAYED_SHORTCUTS_MAP[shortcutKey];

  return preferredDisplayString || shortcutKey.toUpperCase();
}

const _displayOrderPriority: Partial<Record<CommandKeys, number>> = {
  Alt: 3,
  Control: 1,
  Meta: 4,
  Shift: 2,
};

/**
 * Returns a new array with the keys sorted into canonical display order.
 */
export function getCanonicalDisplayOrder(keys: ReadonlyArray<CommandKeys>) {
  const getPriority = (key: CommandKeys) =>
    _displayOrderPriority[key] || Number.MAX_SAFE_INTEGER;

  const keysFiltered = keys?.filter(Boolean) || [];
  const [modifierKeys, plainKeys] = partition(keysFiltered, isModifierKey);
  const result = [
    ...modifierKeys.sort((a, b) => getPriority(a) - getPriority(b)),
    ...plainKeys.sort(),
  ];
  return result;
}

/**
 * Returns an array of valid Action Modifier keys.
 */
export function filterActionModifiers(
  actions: ReadonlyArray<ActionModifiers>
): ReadonlyArray<ActionModifiers> {
  const filteredActions = actions?.filter(Boolean) || [];
  return filteredActions?.filter((action: ActionModifiers) =>
    isActionModifier(action)
  );
}

/**
 * Return a formatted display string representing a keyboard shortcut for
 * the current platform, with automatic platform-dependent stylization,
 * replacement of `Meta` as either `⌘` or `Ctrl`, and canonical display order.
 *
 * @todo (ACE-15): update and reconcile types with vapor
 *
 * @example
 * ```js
 * getKeyboardShortcutString(['Meta', 'a']); // '⌘A' or 'Ctrl+A'
 * ```
 */
export function getKeyboardShortcutString(
  keys: ReadonlyArray<CommandKeys>,
  isApple = false
) {
  return getCanonicalDisplayOrder(keys)
    .map((key) => getPrettyKeyName(key, isApple))
    .filter((key, i, arr) => arr.indexOf(key) === i)
    .join(isApple ? '' : '+');
}

/**
 * Return a formatted array of keys representing a keyboard shortcut for
 * the current platform, with automatic platform-dependent stylization,
 * replacement of `Meta` as either `⌘` or `Ctrl`, and canonical display order.
 *
 *
 * @example
 * ```js
 * getKeyboardShortcutArray(['Meta', 'a']); // ['⌘', 'A'] or ['Ctrl', 'A']
 * ```
 */
export function getKeyboardShortcutArray(
  keys: ReadonlyArray<CommandKeys>,
  isApple = false
) {
  return getCanonicalDisplayOrder(keys)
    .map((key) => getPrettyKeyName(key, isApple))
    .filter((key, i, arr) => arr.indexOf(key) === i);
}

/**
 * Splits a keyboard shortcut string into an array of keys.
 * Primarily developed to support two use cases, Mac and Windows:
 * 1. Concatenated format (delimiter = ''): 'CtrlShiftA' → ['Ctrl', 'Shift', 'A']
 * 2. Delimited format (delimiter = '+'): 'Ctrl+Shift+A' → ['Ctrl', 'Shift', 'A']
 *
 * @example Mac Format (concatenated, no delimiter)
 * splitKeyboardShortcut('CtrlShiftA', '') → ['Ctrl', 'Shift', 'A']
 * splitKeyboardShortcut('Shift', '') → ['Shift']
 * splitKeyboardShortcut('CtrlA', '') → ['Ctrl', 'A']
 *
 * @example Windows Format (with '+' delimiter)
 * splitKeyboardShortcut('Ctrl+Shift+A', '+') → ['Ctrl', 'Shift', 'A']
 * splitKeyboardShortcut('Ctrl+A', '+') → ['Ctrl', 'A']
 *
 * @example Special Cases
 * splitKeyboardShortcut('Ctrl++', '+') → ['Ctrl', '+']    // When '+' is the actual key
 * splitKeyboardShortcut('Ctrl+', '+') → ['Ctrl', '+']     // When '+' is the actual key
 * splitKeyboardShortcut('+', '+') → ['+']                 // Delimiter as a key
 *
 * @param shortcut - The keyboard shortcut string to split
 * @param delimiter - The delimiter between keys:
 *                   '' for Mac format (e.g., 'CtrlShiftA')
 *                   '+' for Windows format (e.g., 'Ctrl+Shift+A')
 * @returns Array of individual keys from the shortcut
 */
export function splitKeyboardShortcut(
  shortcut: string | undefined,
  delimiter = ''
) {
  if (!shortcut) {
    return [''];
  }

  // Handle the case where the input is exactly the delimiter
  // Example: splitKeyboardShortcut('+', '+') → ['+']
  if (shortcut === delimiter) {
    return [shortcut];
  }

  // Try to match a modifier key at the start of the string
  const modifierRegex =
    /^(Shift|Alt|Control|Ctrl|Meta|Command|Backspace|Delete)(.*)$/;
  const match = shortcut.match(modifierRegex);

  // If no modifier is found, split the input by delimiter
  // Example: splitKeyboardShortcut('J', '') → ['J']
  if (!match) {
    return shortcut.split(delimiter).filter(Boolean);
  }

  // Extract the modifier and remaining parts
  const [, modifier, rest] = match;

  // Case 1: Single modifier key with no additional parts
  // Example: splitKeyboardShortcut('Ctrl', '') → ['Ctrl']
  if (!rest && !delimiter) {
    return [modifier];
  }

  // Case 2: The remaining part is the delimiter itself
  // Examples: 'Ctrl+' or 'Ctrl++' where '+' is the actual key
  // Example: splitKeyboardShortcut('Ctrl++', '+') → ['Ctrl', '+']
  if (rest === delimiter || (delimiter && rest === delimiter + delimiter)) {
    return [modifier, delimiter];
  }

  // Case 3: Standard shortcut combinations
  // Split the remaining part and combine with the modifier
  // Example: splitKeyboardShortcut('Ctrl+Shift+A', '+') → ['Ctrl', 'Shift', 'A']
  const remainingKeys = rest.split(delimiter).filter(Boolean);
  return [modifier, ...remainingKeys];
}
